// src/Component/Ui_Components/Button.tsx
//
// Single source of truth for buttons across the app.
//
// Variants (fixed visual style — do not override the visual utilities via
// className; use the appropriate variant + size instead):
//   primary    — filled teal, white text                  (Create account, Verify)
//   secondary  — white bg, gray border, dark text         (Continue with Google, Back)
//   outline    — white bg, teal border, teal text         (Modify, on a read-only card)
//   plan       — deep teal fill, white text                (the pricing card being recommended)
//   planQuiet  — white bg, gray border, dark text; border deepens to teal on hover (the second of a pair of full-width card actions)
//   invite     — pale violet fill, violet border and text (Send pre-consultation)
//   inviteSolid— filled violet, white text                (Send pre-consultation, on the violet banner)
//   approve    — filled green, white text                 (Accept, on a submitted pre-consultation)
//   ghost      — no bg, teal text                         (inline text actions e.g. "Resend")
//   quiet      — no bg, ink text                          (a third action that must not compete, e.g. "View full form")
//   danger     — filled red, white text                   (Delete, Reject)
//   dangerQuiet— white bg, red border and text            (Request refund, on a card)
//
// Sizes:
//   xs — px-3 py-1.5 text-xs    (in-card actions)
//   sm — px-4 py-2  text-sm     (default page action)
//   md — px-6 py-2.5 text-sm 3xl:text-base   (primary page action)
//   lg — px-6 py-3.5 text-[15px]             (auth submits, big touch targets)
//
// Extra behaviors:
//   loading  — overlays a spinner and disables the click. The button keeps its
//              exact size, so a loading toggle never shifts the layout.
//   startIcon / endIcon — icon slots that layout with proper gap.
//   fullWidth — applies w-full.
//   onDisabledClick — what a disabled button answers a press with, for the
//              ones that are off for a reason worth telling. It stays
//              reachable so it can give it; see the note in the component.
//
// className is preserved for LAYOUT overrides only (mt-*, mb-*, flex-1, etc.).
// It is appended last so it wins in the DOM, but do not fight the variant's
// visual utilities from callers — pick the right variant instead.

import React from "react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "plan"
  | "planQuiet"
  | "invite"
  | "inviteSolid"
  | "approve"
  | "ghost"
  | "quiet"
  | "danger"
  | "dangerQuiet";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  /**
   * Called when a disabled button is pressed, instead of it doing nothing.
   * Supplying it changes how `disabled` is enforced — see `softDisabled`
   * below. The same prop `CustomDropdown` takes, and for the same reason: a
   * control that has a reason to give should be able to give it.
   */
  onDisabledClick?: () => void;
}

const baseStyles =
  "relative inline-flex items-center justify-center gap-2 font-medium transition-colors " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 " +
  " disabled:cursor-not-allowed";

const variantStyles: Record<ButtonVariant, string> = {
  primary: "bg-[#00C2CB] text-white hover:bg-[#00b0b8] disabled:text-[#00BACB] disabled:bg-[#D7F9FD] ",
  secondary:
    "bg-white border border-[#E0E0E0] text-[#1F1F1F] hover:border-[#1E1F24] disabled:opacity-50",
  outline: "bg-white border border-[#00C2CB] text-[#00C2CB] hover:bg-[#E6FAFB] disabled:opacity-50",
  // Deeper than the brand teal above on purpose: it sits inside a card that is
  // itself tinted, and the app's `primary` fill is too light to hold against a
  // pale teal one.
  plan: "bg-[#007E8C] text-white hover:bg-[#006A76] disabled:opacity-50",
  // A full-width action that is still a real choice, standing beside a filled
  // one — the price list's second card. At rest the outline gives it the weight
  // of a button without competing with the filled one for the eye; under the
  // pointer only the border deepens to teal, which is enough to say the card is
  // live without a fill that would make it look like the recommended one.
  planQuiet:
    "bg-white border border-[#E0E0E0] text-[#1F1F1F] hover:border-[#00424B] disabled:opacity-50",
  // Violet rather than the brand teal, and the only button in the app that is
  // not. It sends somebody a form to fill in — an action that reaches out to a
  // client rather than changing anything in here — and it stands beside the
  // teal ones that do, on a header or a card, where two buttons of the same
  // colour would read as two halves of one thing. The fill is a tint of its
  // own border, so it holds its shape on white without weighing as much as a
  // filled action.
  invite:
    "bg-[#FAF8FF] border border-[#8563DB] text-[#8563DB] hover:bg-[#F1EBFE] disabled:opacity-50",
  // The same violet, filled, for the one place the outlined one cannot be used:
  // standing on the pale violet card that asks for a client's health details.
  // There the tint above is all but the card's own ground, and a button has to
  // read as the thing to press rather than as a panel inside a panel.
  inviteSolid: "bg-[#8563DB] text-white hover:bg-[#7452CC] disabled:opacity-50",
  // Green, and the only green button in the app. It stands beside a red one on
  // the strip that asks whether a client's answers are right, and that pair has
  // to read as yes-or-no at a glance — the brand teal against a red would read
  // as an action beside a warning instead. Filled where the Reject beside it is
  // outlined, because accepting is the ordinary outcome and rejecting is not.
  approve: "bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-50",
  ghost: "bg-transparent text-[#00C2CB] hover:bg-gray-50 disabled:opacity-50",
  // The same nothing as `ghost`, in the text colour rather than the brand's.
  // For a third action standing beside a decisive pair — opening the form to
  // read it is not a choice about the form, and a teal one would make three
  // things competing where there are two.
  quiet: "bg-transparent text-ink hover:bg-black/5 disabled:opacity-50",
  danger: "bg-[#FF3B30] text-white disabled:opacity-50",
  // A destructive action that is not a warning. It sits on a plain card among
  // ordinary ones, so it carries the red as an outline rather than as a fill:
  // enough for the eye to know what kind of thing it is, not enough to make
  // the card look like something has gone wrong.
  dangerQuiet:
    "bg-white border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FEF2F2] disabled:opacity-50",
};

const sizeStyles: Record<ButtonSize, string> = {
  xs: "px-3 py-1.5 text-xs rounded-md",
  sm: "px-4 py-2 text-sm rounded-[6px]",
  md: "px-6 py-2.5 text-sm 3xl:text-base rounded-[6px]",
  lg: "px-6 py-3.5 text-[15px] rounded-lg",
};

/**
 * The classes a button of this variant and size wears, for the one thing that
 * cannot be a `<button>`: a link.
 *
 * Going somewhere is a link's job — it belongs in the accessibility tree as
 * one, it opens in a new tab on a middle click, and a screen reader announces
 * where it goes. But a "Back to my clients" beside an empty state is still the
 * screen's main action and has to look like one. So the look lives here, and
 * `<Link className={buttonClasses(...)}>` wears it without a second copy of it
 * drifting out of step with the component.
 *
 * For anything that acts rather than navigates, use `Button` — this does not
 * carry the loading spinner or the disabled handling, because a link has
 * neither.
 */
export const buttonClasses = ({
  variant = "primary",
  size = "sm",
  fullWidth = false,
  className = "",
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
} = {}): string =>
  [
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    fullWidth ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

const Spinner: React.FC = () => (
  <span
    aria-hidden="true"
    className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
  />
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "sm",
      loading = false,
      startIcon,
      endIcon,
      fullWidth = false,
      className = "",
      children,
      disabled,
      onDisabledClick,
      onClick,
      type = "button",
      ...rest
    },
    ref
  ) => {
    // A disabled button with something to say cannot carry the native
    // attribute: the browser dispatches nothing to one — not even to the
    // elements around it — so the press that would ask why never lands. It is
    // marked `aria-disabled` instead, which reads the same to a screen reader,
    // and the press is turned back by hand below. Without a reason to give,
    // `disabled` stays the plain thing. Loading is always the plain thing:
    // there is nothing to explain about a button already doing the work.
    const softDisabled = !!disabled && !loading && !!onDisabledClick;

    const classes = [
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      fullWidth ? "w-full" : "",
      // The look the `disabled:` utilities give the native one, which never
      // match a button that is only disabled to the reader.
      softDisabled ? "opacity-50 cursor-not-allowed" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        ref={ref}
        type={type}
        disabled={(disabled && !softDisabled) || loading}
        aria-disabled={disabled || loading || undefined}
        // Both the pointer and the keyboard arrive here — Enter and Space on a
        // focused button are a click — so the reason is given however the
        // button was reached for.
        onClick={softDisabled ? () => onDisabledClick?.() : onClick}
        className={classes}
        {...rest}
      >
        {/*
          While loading, the label stays in the layout but hidden, and the
          spinner is overlaid on top. Swapping the label out for the spinner
          instead would resize the button to the spinner's box — the label is
          what reserves the button's height and width.
        */}
        <span
          className={`inline-flex items-center gap-2 ${
            loading ? "invisible" : ""
          }`}
        >
          {startIcon}
          {children}
          {endIcon}
        </span>
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner />
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
